import { Avatar, Menu, Text } from "@mantine/core";
import { useThrottledCallback } from "@mantine/hooks";
import IconLamp from "@src/assets/icons/lamp.svg?react";
import IconChevronDown from "@src/assets/icons/lucide/chevron-down.svg?react";
import IconLayoutSidebarLeftCollapse from "@src/assets/icons/tabler/layout-sidebar-left-collapse.svg?react";
import { ROUTES } from "@src/common/constants";
import { useMenuState } from "@src/components/layouts/menu/menu-drawer";
import { openPlanModal } from "@src/components/plan/plan-modal/plan-modal";
import { useMyPlanSuspense } from "@src/components/plan/queries/fetch-plan";
import { usePlanStatus } from "@src/components/plan/subscriptions/plan-status";
import { translatePlanName } from "@src/components/plan/translations";
import { NativeLikeTooltip as Tooltip } from "@src/components/ui/native-like-tooltip";
import config from "@src/config";
import {
	DesktopProfileBadge,
	DesktopUserMenuItems,
	useDesktopProfiles,
} from "@src/desktop";
import { PlanFamily, PlanStatus, SupportedPlan } from "@src/gql/graphql";
import { useEmailVerificationStatus } from "@src/hooks/use-email-verification-status";
import clsx from "clsx";
import {
	type ComponentPropsWithoutRef,
	type ReactNode,
	forwardRef,
	useCallback,
} from "react";
import { NavLink } from "react-router-dom";
import { useAuthenticatedUser } from "../user/user-context";

function PlanPill() {
	const { data } = useMyPlanSuspense();
	const isBeliever = data.plan === SupportedPlan.Believer;
	const isStarter = data.planFamily === PlanFamily.Starter;
	const isNotActive = data.status !== PlanStatus.Active;
	const isCancelled = data.status === PlanStatus.Cancelled;
	const canUpgrade = !isBeliever && (isStarter || isNotActive || isCancelled);

	const planLabel = translatePlanName({
		plan: data.plan,
		planFamily: data.planFamily,
		status: data.status,
	});

	const handleClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		e.stopPropagation();
		openPlanModal();
	}, []);

	if (isBeliever) {
		return <BelieverManageButton />;
	}

	if (!canUpgrade) {
		return (
			<span
				title={planLabel}
				className={clsx(
					"inline-flex shrink-0 items-center gap-1 rounded-md px-1.5 py-0.5",
					"text-[11px] font-medium leading-none",
					"border border-border-subtle bg-surface-subtle text-text-secondary",
				)}
			>
				{planLabel}
			</span>
		);
	}

	return (
		<button
			type="button"
			onClick={handleClick}
			title={`${planLabel} — Click to upgrade`}
			className={clsx(
				"inline-flex shrink-0 items-center gap-1 rounded-md px-1.5 py-0.5",
				"text-[11px] font-medium leading-none transition-colors cursor-pointer",
				"border border-brand-primary/25 bg-brand-primary/[0.08]",
				"text-brand-primary hover:bg-brand-primary/15",
			)}
		>
			<IconLamp className="h-2.5 w-2.5 shrink-0" />
			<span>{planLabel}</span>
		</button>
	);
}

function BelieverManageButton() {
	return (
		<a
			href={config.enterpriseContactUrl}
			target="_blank"
			rel="noopener noreferrer"
			onClick={(e) => e.stopPropagation()}
			className={clsx(
				"inline-flex items-center rounded-md px-1.5 py-0.5",
				"text-[11px] leading-none text-text-secondary",
				"border border-border-default hover:bg-surface-subtle",
				"transition-colors",
			)}
		>
			Manage
		</a>
	);
}

function UserInfo() {
	const authenticatedUser = useAuthenticatedUser();

	usePlanStatus();

	return (
		<div className="flex items-center gap-1.5 min-w-0 grow">
			<Text
				size="sm"
				fw={600}
				className="overflow-hidden whitespace-nowrap text-ellipsis leading-tight"
			>
				{authenticatedUser.preferredName}
			</Text>
			<PlanPill />
		</div>
	);
}

const HeaderIconButton = forwardRef<
	HTMLButtonElement,
	{
		ariaLabel: string;
		children: ReactNode;
	} & Omit<ComponentPropsWithoutRef<"button">, "children" | "type">
>(function HeaderIconButton({ ariaLabel, children, className, ...rest }, ref) {
	return (
		<button
			ref={ref}
			type="button"
			aria-label={ariaLabel}
			className={clsx(
				"flex shrink-0 items-center justify-center",
				"h-7 w-7 rounded-md cursor-pointer",
				"border border-transparent bg-transparent",
				"text-text-secondary hover:text-text-primary",
				"hover:bg-surface-hover-subtle",
				"transition-colors",
				className,
			)}
			{...rest}
		>
			{children}
		</button>
	);
});

function ProfileSwitcherButton() {
	const { isElectron, profiles, currentProfile } = useDesktopProfiles();

	if (!isElectron || profiles.length === 0) {
		return null;
	}

	return (
		<Menu shadow="md" width={240} position="bottom-start" zIndex={9999}>
			<Menu.Target>
				<HeaderIconButton ariaLabel="Switch profile">
					<IconChevronDown width={14} height={14} />
				</HeaderIconButton>
			</Menu.Target>

			<Menu.Dropdown>
				<DesktopUserMenuItems currentAvatarUrl={currentProfile?.avatarUrl} />
			</Menu.Dropdown>
		</Menu>
	);
}

export function UserButton(props: {
	className?: string;
	rightSlot?: ReactNode;
}) {
	const authenticatedUser = useAuthenticatedUser();
	const { hasVerificationPending } = useEmailVerificationStatus();
	const { toggleDrawer } = useMenuState();
	const handleCollapsePointerDown = useThrottledCallback(toggleDrawer, 100);

	return (
		<div className="flex items-center justify-between gap-1 w-full h-9">
			<NavLink
				className={clsx(
					"flex-1 min-w-0 flex flex-row items-center",
					"text-text-primary rounded-lg border border-solid",
					"border-transparent cursor-pointer",
					"hover:bg-surface-hover-subtle",
					"transition-colors",
					props.className,
				)}
				to={ROUTES.profile}
			>
				<div className="relative shrink-0 mr-2">
					<Avatar
						src={authenticatedUser.avatarUrl}
						size="26px"
						className={clsx(
							"relative before:absolute before:inset-0",
							"before:rounded-full before:border",
							"before:border-border-subtle",
						)}
					/>
					{hasVerificationPending && (
						<span
							className={clsx(
								"absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full",
								"border border-surface-default bg-yellow-400",
							)}
						/>
					)}
					<DesktopProfileBadge />
				</div>

				<UserInfo />
			</NavLink>

			<div className="flex items-center gap-0.5 shrink-0">
				{props.rightSlot}
				<ProfileSwitcherButton />

				<Tooltip label="Close navbar" openDelay={300} withArrow>
					<HeaderIconButton
						ariaLabel="Close navbar"
						onPointerDown={handleCollapsePointerDown}
					>
						<IconLayoutSidebarLeftCollapse
							width={18}
							height={18}
							strokeWidth={1.5}
						/>
					</HeaderIconButton>
				</Tooltip>
			</div>
		</div>
	);
}
